/**
 * QuotePopover - Floating button that appears when text is selected in research content.
 *
 * Listens for text selection within a container element and shows a small
 * "Ask about this" button near the selection. Clicking it sends the selected
 * text to the chat input as a blockquote.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote } from 'lucide-react';

interface QuotePopoverProps {
  /** Ref to the container element where text selection should be tracked */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Callback when user clicks the quote button */
  onQuote: (text: string) => void;
}

interface PopoverPosition {
  top: number;
  left: number;
}

export function QuotePopover({ containerRef, onQuote }: QuotePopoverProps) {
  const [selectedText, setSelectedText] = useState('');
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const clearSelection = useCallback(() => {
    setSelectedText('');
    setPosition(null);
  }, []);

  // Handle mouseup to check for text selection
  const handleMouseUp = useCallback(() => {
    // Small delay to let the browser finalize the selection
    requestAnimationFrame(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || !selection.toString().trim()) {
        clearSelection();
        return;
      }

      const text = selection.toString().trim();
      if (text.length < 3) {
        clearSelection();
        return;
      }

      // Check that the selection is within our container
      const container = containerRef.current;
      if (!container) {
        clearSelection();
        return;
      }

      const anchorNode = selection.anchorNode;
      const focusNode = selection.focusNode;
      if (!anchorNode || !focusNode || !container.contains(anchorNode) || !container.contains(focusNode)) {
        clearSelection();
        return;
      }

      // Get position from the selection range
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      // Position the popover above the selection, centered horizontally
      setPosition({
        top: rect.top + window.scrollY - 8,
        left: rect.left + window.scrollX + rect.width / 2,
      });
      setSelectedText(text);
    });
  }, [containerRef, clearSelection]);

  // Dismiss on scroll or click outside
  useEffect(() => {
    if (!selectedText) return;

    const handleScroll = () => clearSelection();
    const handleMouseDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        clearSelection();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [selectedText, clearSelection]);

  // Attach mouseup listener to container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
  }, [containerRef, handleMouseUp]);

  const handleQuote = useCallback(() => {
    if (selectedText) {
      onQuote(selectedText);
      window.getSelection()?.removeAllRanges();
      clearSelection();
    }
  }, [selectedText, onQuote, clearSelection]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {selectedText && position && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 4 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="fixed z-[9999]"
          style={{
            top: position.top,
            left: position.left,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <button
            onClick={handleQuote}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-raised border border-border-subtle shadow-xl text-sm text-text-primary hover:text-text-primary hover:bg-surface-overlay hover:border-border-light transition-colors cursor-pointer"
          >
            <Quote className="w-3.5 h-3.5 text-verdict-primary" />
            <span className="text-xs font-medium">Ask about this</span>
          </button>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
